
/**
 * Composant permettant de saisir directement un numéro de page.
 * 
 * @component
 * @param {Object} props - Les propriétés du composant
 * @param {number} props.currentPage - La page actuelle
 * @param {number} props.totalPages - Le nombre total de pages
 * @param {function} props.onPageChange - Fonction appelée lors du changement de page
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface PaginationInputProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const PaginationInput = React.memo(({
  currentPage,
  totalPages,
  onPageChange
}: PaginationInputProps) => {
  const [pageInput, setPageInput] = useState<string>((currentPage + 1).toString());

  const handlePageInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInput(e.target.value);
  }, []);

  const handlePageInputSubmit = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const newPage = parseInt(pageInput) - 1;
      if (!isNaN(newPage) && newPage >= 0 && newPage < totalPages) {
        onPageChange(newPage);
      } else {
        setPageInput((currentPage + 1).toString());
      }
    }
  }, [pageInput, totalPages, onPageChange, currentPage]);

  useEffect(() => {
    setPageInput((currentPage + 1).toString());
  }, [currentPage]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div 
          role="group" 
          aria-label="Navigation directe vers une page"
        >
          <Input
            type="number"
            min={1}
            max={totalPages}
            value={pageInput}
            onChange={handlePageInputChange}
            onKeyDown={handlePageInputSubmit}
            className="w-16 h-8 sm:h-10 text-center"
            placeholder="Page"
            aria-label="Saisir un numéro de page"
            aria-valuemin={1}
            aria-valuemax={totalPages}
            aria-valuenow={parseInt(pageInput)}
            aria-valuetext={`Page ${pageInput} sur ${totalPages}`}
            title="Appuyez sur Entrée pour valider"
          />
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p>Entrez un numéro de page et appuyez sur Entrée</p>
      </TooltipContent>
    </Tooltip>
  );
});

PaginationInput.displayName = 'PaginationInput';
